import type { Criterion, Enterprise, ManualScores, RefLine, RefPrestation } from '../types';

// Formules de notation prix usuelles des marchés publics français.
// - inverse       : N = scale × Pmin / P                                 (moins-disant, proportionnelle)
// - linear        : N = scale × (Pmax − P) / (Pmax − Pmin)               (linéaire min-max ; meilleur=scale, pire=0)
// - subtractive   : N = scale × (1 − (P − Pmin) / Pmin), clamp ≥ 0       (soustractive par rapport au mini)
export type PriceFormula = 'inverse' | 'linear' | 'subtractive';

export interface EnterpriseStats {
  name: string;
  totalDeclare: number;
  totalRecalcule: number;
  ecart: number;
  ecartPct: number;
  score: number;
  rank: number;
  isOAB: boolean;
  // Multi-critères — vides si pas de critères fournis.
  criterionScores: Record<string, number>; // /20 par critère
  finalScore: number;                       // Σ (weight% × subScore) — /20
  finalRank: number;                        // 0 si pas de critères
}

const isPrestation = (r: RefLine): r is RefPrestation => !r.isSection;

// Total recalculé strict : Σ (qty × pu). Ignore l'éventuel `offer.total` annoncé par le contractant
// (qui peut être incorrect — c'est justement ce qu'on veut détecter).
export function computeRecalculatedTotal(
  enterprise: Enterprise,
  lines: RefLine[],
  optionalIds: Set<string>,
): number {
  return lines.reduce((acc, line) => {
    if (!isPrestation(line)) return acc;
    if (optionalIds.has(line.id)) return acc;
    const offer = line.offers[enterprise.name];
    if (!offer) return acc;
    return acc + offer.qty * offer.pu;
  }, 0);
}

// Double-moyenne 20% (art. R. 2152-3 Code de la commande publique).
// 1) M1 = moyenne des totaux. 2) On ne garde que les totaux dans [0.8·M1, 1.2·M1].
// 3) M2 = moyenne du sous-ensemble. 4) Seuil = 0.8·M2. Tout total sous le seuil est flaggé OAB.
export function detectOAB(totals: number[]): boolean[] {
  if (totals.length < 3) return totals.map(() => false);
  const m1 = totals.reduce((a, b) => a + b, 0) / totals.length;
  const kept = totals.filter(t => t >= 0.8 * m1 && t <= 1.2 * m1);
  if (kept.length === 0) return totals.map(() => false);
  const m2 = kept.reduce((a, b) => a + b, 0) / kept.length;
  const threshold = 0.8 * m2;
  return totals.map(t => t < threshold);
}

export function computeEnterpriseStats(
  enterprises: Enterprise[],
  lines: RefLine[],
  optionalIds: Set<string>,
  criteria?: Criterion[],
  manualScores?: ManualScores,
  scale: number = 20,
  formula: PriceFormula = 'inverse',
): EnterpriseStats[] {
  const totals = enterprises.map(ent => computeRecalculatedTotal(ent, lines, optionalIds));
  const positiveTotals = totals.filter(t => t > 0);
  const minTotal = positiveTotals.length > 0 ? Math.min(...positiveTotals) : 0;
  const maxTotal = positiveTotals.length > 0 ? Math.max(...positiveTotals) : 0;
  const oabFlags = detectOAB(totals);

  const ranked = enterprises
    .map((ent, i) => ({ name: ent.name, total: totals[i] }))
    .filter(x => x.total > 0)
    .sort((a, b) => a.total - b.total);
  const rankMap = new Map<string, number>();
  ranked.forEach((item, idx) => rankMap.set(item.name, idx + 1));

  const scoreOf = (p: number): number => {
    if (p <= 0 || minTotal <= 0) return 0;
    if (formula === 'linear') {
      if (maxTotal <= minTotal) return scale;
      return scale * ((maxTotal - p) / (maxTotal - minTotal));
    }
    if (formula === 'subtractive') {
      return Math.max(0, scale * (1 - (p - minTotal) / minTotal));
    }
    return scale * (minTotal / p);
  };

  const base = enterprises.map((ent, i) => {
    const totalRecalcule = totals[i];
    const totalDeclare = ent.totalHT;
    const ecart = totalDeclare - totalRecalcule;
    const ecartPct = totalDeclare > 0 ? (ecart / totalDeclare) * 100 : 0;
    const score = scoreOf(totalRecalcule);
    return {
      name: ent.name,
      totalDeclare,
      totalRecalcule,
      ecart,
      ecartPct,
      score,
      rank: rankMap.get(ent.name) ?? 0,
      isOAB: oabFlags[i],
    };
  });

  // Sans critères : scores vides, pas de rang final.
  if (!criteria || criteria.length === 0) {
    return base.map(b => ({ ...b, criterionScores: {}, finalScore: 0, finalRank: 0 }));
  }

  // Calcul critère par critère.
  const withCriteria = base.map(b => {
    const criterionScores: Record<string, number> = {};
    let finalScore = 0;
    for (const c of criteria) {
      const sub = c.auto
        ? b.score
        : (manualScores?.[b.name]?.[c.id] ?? 0);
      criterionScores[c.id] = sub;
      finalScore += (c.weight / 100) * sub;
    }
    return { ...b, criterionScores, finalScore };
  });

  // Rang final décroissant (plus grand finalScore = meilleur).
  const sortedFinal = withCriteria
    .map(b => ({ name: b.name, finalScore: b.finalScore }))
    .slice()
    .sort((a, b) => b.finalScore - a.finalScore);
  const finalRankMap = new Map<string, number>();
  sortedFinal.forEach((item, idx) => finalRankMap.set(item.name, idx + 1));

  return withCriteria.map(b => ({ ...b, finalRank: finalRankMap.get(b.name) ?? 0 }));
}
